import { useState, useRef, useEffect } from "react";

const ItineraryChatbot = ({ onSendMessage, isProcessing, messages, hasModifications, onRestoreOriginal, onClose }) => {
  const [inputValue, setInputValue] = useState("");
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputValue.trim() && !isProcessing) {
      onSendMessage(inputValue.trim());
      setInputValue("");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const suggestionChips = [
    { label: "4 nights / 5 days", icon: "📅" },
    { label: "Add beach day", icon: "🏖️" },
    { label: "More adventure", icon: "🎯" },
  ];

  const handleSuggestionClick = (suggestion) => {
    if (!isProcessing) {
      onSendMessage(suggestion);
    }
  };

  const formatMessage = (content, type) => {
    if (type === "error" && content.length > 150) {
      const errorMatch = content.match(/Sorry, I couldn't make that change\./);
      if (errorMatch) {
        return "Sorry, I couldn't make that change. Please try a simpler request.";
      }
      return content.substring(0, 150) + "...";
    }
    return content;
  };

  return (
    <div className="flex flex-col h-full bg-teal-900/40 backdrop-blur-md rounded-2xl border border-white/10 shadow-2xl p-4 lg:p-5">
      <div className="flex-shrink-0 mb-4 pb-3 border-b border-teal-400/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
              <svg className="w-4 h-4 text-teal-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-white">Edit Itinerary</h3>
              <p className="text-teal-200/70 text-xs">AI-powered modifications</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {hasModifications && onRestoreOriginal && (
              <button
                onClick={onRestoreOriginal}
                disabled={isProcessing}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 text-teal-100 text-xs rounded-full transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed border border-teal-400/20 hover:border-teal-400/40"
                title="Restore original itinerary"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                </svg>
                <span className="hidden sm:inline">Restore Original</span>
              </button>
            )}
            {onClose && (
              <button
                onClick={onClose}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
                title="Close chat"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-hidden mb-3 space-y-3 pr-1 scrollbar-thin min-h-0">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-2 h-full min-h-0 text-center">
            <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center mb-2 shrink-0">
              <svg className="w-6 h-6 text-teal-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <p className="text-teal-100 text-sm font-medium mb-1">How can I help?</p>
            <p className="text-teal-200/60 text-xs">Type a change or try a suggestion</p>
          </div>
        ) : (
          messages.map((msg, index) => (
            <div
              key={index}
              className={`flex ${msg.type === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm break-words shadow-sm ${
                  msg.type === "user"
                    ? "bg-white text-gray-800 rounded-br-md"
                    : msg.type === "error"
                    ? "bg-red-500/20 text-red-100 rounded-bl-md border border-red-400/30"
                    : "bg-teal-700/50 text-white rounded-bl-md border border-teal-500/20"
                }`}
              >
                {msg.type === "assistant" && (
                  <div className="flex items-center gap-1.5 mb-1.5 text-teal-300 text-xs font-medium">
                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                    Done
                  </div>
                )}
                {msg.type === "error" && (
                  <div className="flex items-center gap-1.5 mb-1.5 text-red-300 text-xs font-medium">
                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                    </svg>
                    Error
                  </div>
                )}
                <span className="whitespace-pre-wrap leading-relaxed">{formatMessage(msg.content, msg.type)}</span>
              </div>
            </div>
          ))
        )}

        {isProcessing && (
          <div className="flex justify-start">
            <div className="bg-teal-700/50 rounded-2xl rounded-bl-md px-4 py-3 border border-teal-500/20 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-teal-300 rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></div>
                  <div className="w-2 h-2 bg-teal-300 rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></div>
                  <div className="w-2 h-2 bg-teal-300 rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></div>
                </div>
                <span className="text-teal-200 text-sm">Updating...</span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {messages.length === 0 && (
        <div className="flex-shrink-0 mb-3 pt-2 border-t border-teal-400/10">
          <p className="text-teal-200/60 text-xs mb-2 font-medium">Quick suggestions</p>
          <div className="flex flex-wrap gap-2">
            {suggestionChips.map((chip, index) => (
              <button
                key={index}
                onClick={() => handleSuggestionClick(chip.label)}
                disabled={isProcessing}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 text-teal-100 text-xs rounded-full transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed border border-teal-400/20 hover:border-teal-400/40"
              >
                <span>{chip.icon}</span>
                <span>{chip.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex-shrink-0 mt-auto pt-3 border-t border-teal-400/20">
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="E.g., 'Add Eiffel Tower to day 2'"
            disabled={isProcessing}
            className="w-full px-4 py-3 pr-12 bg-white/10 hover:bg-white/15 border border-teal-400/30 focus:border-teal-300/50 rounded-xl text-white placeholder-teal-200/50 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300/30 disabled:opacity-50 transition-all duration-200"
          />
          <button
            type="submit"
            disabled={!inputValue.trim() || isProcessing}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-white hover:bg-teal-50 text-teal-600 rounded-lg transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white shadow-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </button>
        </div>
        <p className="text-teal-200/50 text-xs mt-2 text-center">
          Press Enter to send
        </p>
      </form>
    </div>
  );
};

export default ItineraryChatbot;
